import React from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/useAuthStore';
import { useLinkedTenants } from '@invoiceflow/portal-core';
import { useAuthStore as useStore } from '../stores/useAuthStore';

/**
 * Only rendered when an account is linked to more than one tenant — the common
 * single-tenant customer should never have to think about tenants at all.
 */
export const TenantSwitcher: React.FC = () => {
  const setTenants = useStore(state => state.setTenants);
  const { data: tenants = [] } = useLinkedTenants({ onLoaded: setTenants });
  const activeTenantId = useAuthStore(state => state.activeTenantId);
  const setActiveTenant = useAuthStore(state => state.setActiveTenant);
  const queryClient = useQueryClient();

  if (tenants.length < 2) return null;

  const active = tenants.find(tenant => tenant.tenantId === activeTenantId);

  const switchTo = (tenantId: string) => {
    if (tenantId === activeTenantId) return;
    setActiveTenant(tenantId);
    // Every cached response belongs to the previous tenant.
    queryClient.clear();
  };

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold text-content-secondary hover:bg-surface-muted transition-colors max-w-[14rem]">
        <Building2 size={16} className="shrink-0 text-content-muted" />
        <span className="truncate">{active?.name || 'Select company'}</span>
        <ChevronDown size={14} className="shrink-0 text-content-muted" />
      </Menu.Button>

      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 mt-2 w-64 origin-top-left bg-surface rounded-2xl shadow-xl ring-1 ring-content ring-opacity-5 focus:outline-none p-2 border border-line-subtle z-40">
          <p className="px-3 py-2 text-[10px] font-black text-content-muted uppercase tracking-widest">
            Your companies
          </p>
          {tenants.map(tenant => (
            <Menu.Item key={tenant.tenantId}>
              {({ active: highlighted }) => (
                <button
                  onClick={() => switchTo(tenant.tenantId)}
                  className={`${
                    highlighted ? 'bg-surface-muted' : ''
                  } flex w-full items-center justify-between px-3 py-2.5 text-sm font-bold rounded-xl transition-colors text-content-secondary`}
                >
                  <span className="truncate">{tenant.name || tenant.tenantId}</span>
                  {tenant.tenantId === activeTenantId && (
                    <Check size={16} className="shrink-0 text-brand" />
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};
